import { Box } from "@mui/material";
import React, { useState, useEffect } from "react";
import { Transition } from "react-transition-group";
import { formatNumber } from "../../../helpers/formatter";
import { formatKpiPercent } from "../../../helpers/scoreAggregate";
import ReportsDoughnutChart from "../../../examples/Charts/DoughnutCharts/ReportsDoughnutChart";
import SoftTypography from "../../SoftTypography";
import SoftProfile from "../../ProfilePictures/SoftProfile";
import HelperVideo from "../../HelperVideo";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import SoftBox from "../../SoftBox";

// Import from material ui
import RankIcon from "../../Icons/RankIcon";
import "./leaderboardStyles.css";

const duration = 250;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
};

const transitionStyles = {
  entering: { opacity: 0.01 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
};

const coloredBarClasses = [
  "coloredProgress1",
  "coloredProgress2",
  "coloredProgress3",
];

function ProgressBar({
  top,
  userId,
  imgsrc,
  userFirstName,
  userLastName,
  isOwnId,
  groupKpis,
  userKpis,
  position,
  prevPosition,
  showRankMove = false,
  isActive,
  isPointsPercent = false,
  onSelect,
  isYeti,
  isViewOnly = false,
  selectable = true,
  darkMode = false,
  height,
  fullColor = false,
  playerStatus,
  // dense: fixed-pixel row spacing for the dashboard's standalone list —
  // the default percentage paddings scale with card WIDTH and blow the
  // rows apart on wide cards.
  dense = false,
  /* Period Yeti scores (today/week/custom/…) so KPI badges match the board
     window instead of always comparing to UseYetiStats month. */
  versusYetiScores = null,
}) {
  const [show, setShow] = useState(false);
  const points = Math.round(userKpis.total);
  /* Guard top===0 — otherwise width becomes "Infinity%" and paints a solid
     black slab across the row (seen on custom ranges when only Yeti has pts
     but top briefly resolves as 0, or all players are 0). */
  const barProgress = top > 0 && userKpis.total
    ? Math.min(100, Math.round((userKpis.total / top) * 100))
    : 0;

  // Chart Section
  const [kpiTitle, setKpiTitle] = useState([]);
  const [kpiValue, setKpiValue] = useState([]);             // real points (drives the list + center total)
  const [kpiDisplayWeights, setKpiDisplayWeights] = useState([]); // floored values that drive the donut slice angles so tiny KPIs stay visible
  const [kpiLabels, setKpiLabels] = useState([]);           // formatted amount strings (e.g. "$4,231")
  const [kpiIds, setKpiIds] = useState([]);
  const [kpiTypes, setKpiTypes] = useState([]);
  const [kpiNormalValues, setKpiNormalValues] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const getKpiInfo = () => {
      const kpiNames = [];
      const kpiPoints = [];
      const kpiAmount = [];
      const kpiId = [];
      const kpiType = [];
      const kpiNormalValue = [];

      if (groupKpis?.length) {
        groupKpis
          .filter((kpi) => !kpi.isDraft)
          /* Show every live metric — including zeros — so the expand list
             matches the board columns. Empty slices stay off the donut via
             displayWeights (p > 0) below; the list still prints 0. */
          .forEach((kpi) => {
            // Keep pts raw (don't round to integer) so the tooltip can
            // show fractional values like "Review: 0.26 pts" for KPIs
            // whose pointScale/pointValue ratio produces decimal points.
            const pts = Number(userKpis[kpi._id]?.pts) || 0;
            const amt =
              userKpis[kpi._id] && userKpis[kpi._id].amt < 1 // Check if decimal / percentage value
                ? userKpis[kpi._id].amt
                : Math.round(userKpis[kpi._id]?.amt || 0);

            kpiNormalValue.push(amt);
            kpiType.push(kpi.kind);
            kpiNames.push(kpi.title);
            kpiPoints.push(pts);
            kpiId.push(kpi._id);
            kpiAmount.push(kpi.kind === "%" ? formatKpiPercent(kpi, amt) : formatNumber(amt, kpi.kind));
          });

        // Build display weights for the donut so every visible metric has
        // a recognisable slice. A pure point-proportional chart hides small
        // KPIs (e.g. a 0.26-pt Review next to a 4,500-pt Revenue) — by
        // flooring each slice to a minimum share of the largest, every
        // metric the player has earned points on stays visible. The real
        // points are still surfaced through the list below and the tooltip.
        const maxPts = kpiPoints.length ? Math.max(...kpiPoints) : 0;
        const minSlice = maxPts > 0 ? maxPts * 0.18 : 0; // ≥18% of the leader
        const displayWeights = kpiPoints.map((p) =>
          p > 0 ? Math.max(p, minSlice) : 0,
        );

        setKpiTitle(kpiNames);
        setKpiValue(kpiPoints);
        setKpiDisplayWeights(displayWeights);
        setKpiLabels(kpiAmount);
        setKpiIds(kpiId);
        setKpiTypes(kpiType);
        setKpiNormalValues(kpiNormalValue);
      } else {
        // Use dummy perfect values for the yeti
        setKpiTitle(["Yeti KPIs"]);
        setKpiValue([100]);
        setKpiDisplayWeights([100]);
        setKpiLabels(["100%"]);
      }
    };

    getKpiInfo();
  }, [groupKpis, userKpis]);

  const handleSelect = () => {
    const shouldShow = !show;
    setShow(shouldShow);
    onSelect?.(shouldShow);
  };

  useEffect(() => {
    setShow((currentShow) => currentShow && isActive);
  }, [isActive]);

  // When the KPI panel opens on a bottom row, the scrolling card (or grid
  // cell) can leave it cut off below the fold — bring it into view once the
  // expand transition has mounted it.
  const expandRef = React.useRef(null);
  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => {
      expandRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, duration + 50);
    return () => clearTimeout(t);
  }, [show]);

  return (
    <SoftBox sx={{ height: height ?? "auto" }}>
      <section
        className={`${dense ? "mb-[4px] py-[7px] px-[10px]" : "mb-[1%] py-[2%] px-[2%]"} rounded-full
          ${selectable && "hover:cursor-pointer"}
        `}
        // Yeti / inactive rows sit on a faint fill. Tokenized so it goes graphite
        // on the dark TV (var undefined off-TV → the original gray-100).
        style={{ background: (isYeti || playerStatus === false) ? "var(--tvd-tile, #F3F4F6)" : "transparent" }}
        {...(selectable && { onClick: handleSelect })}
      >
        <Box className="flex items-center gap-[1%] w-full">
          <Box className="flex flex-col items-center min-w-[2rem]">
            <SoftTypography
              className="font-semibold"
              color={darkMode ? "white" : "black"}
              sx={{ color: "var(--tvd-ink, #0F172A)", fontSize: dense ? "12.5px" : "1.75vh", ...(dense && { fontFamily: "DM Sans", fontWeight: 600 }) }}
            >
              {position}
            </SoftTypography>
            {showRankMove && (
              <RankIcon
                difference={prevPosition - position}
                darkMode={darkMode}
              />
            )}
          </Box>
          <Box
            className="grow flex items-center gap-[1%] relative z-0"
            sx={{ padding: fullColor ? "1% 2% 1% 1%" : "0" }}
          >
            {/* Progress bar for full-color mode */}
            {fullColor && (
              <Box
                className={`
                w-full h-full absolute top-0 left-0 z-10
                ${
                  position <= coloredBarClasses.length
                    ? coloredBarClasses[position - 1]
                    : ""
                }
              `}
                // Set minimum bar progress
                sx={{ width: `${barProgress < 12 ? 12 : barProgress}%` }}
              />
            )}
            <SoftProfile
              size={dense ? "md" : "lg"}
              src={imgsrc}
              name={userFirstName}
              glow={isOwnId}
              sx={{ zIndex: 20 }}
              fullCover
            />
            <Box className="grow flex flex-col z-20" sx={{ minWidth: 0 }}>
              <Box className="flex items-center justify-between" sx={{ minWidth: 0 }}>
                <SoftTypography
                  color={darkMode ? "white" : "dark"}
                  sx={{
                    fontFamily: "DM Sans",
                    color: "var(--tvd-ink, #1D2939)",
                    fontSize: dense ? "13px" : "14px",
                    fontWeight: dense ? 500 : 600,
                    letterSpacing: "-0.005em",
                    marginBottom: fullColor ? "-1vh" : 0,
                    // Truncate instead of colliding with/wrapping under the
                    // points value on narrow (dense/mobile) rows.
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {`${
                    userFirstName
                      ? userFirstName + " " + userLastName
                      : "Unregistered User"
                  }`}
                </SoftTypography>
                <SoftTypography
                  color={darkMode ? "white" : "dark"}
                  sx={{
                    fontFamily: "DM Sans",
                    color: "var(--tvd-ink, #0F172A)",
                    textAlign: "center",
                    marginLeft: "1%",
                    lineHeight: 1.25,
                    fontWeight: 600,
                    fontSize: dense || isMobile ? "14px" : "16px",
                    fontFeatureSettings: '"tnum" 1',
                    letterSpacing: "-0.01em",
                    flexShrink: 0,
                  }}
                >
                  {points} {isPointsPercent ? "%" : ""}
                </SoftTypography>
              </Box>
              <Box className="flex items-center gap-1 w-full">
                {/* Alternate progress bar - partial and not colored */}
                <Box
                  sx={{
                    height: "1vh",
                    borderRadius: "1vh",
                    overflow: "hidden",
                    flexGrow: 1,
                  }}
                >
                  {!fullColor && (
                    <Box
                      className="h-[1vh] transition-[width] duration-[600ms] ease-linear rounded-tr-[1vh] rounded-br-[1vh]"
                      sx={{
                        width: `${barProgress}%`,
                        backgroundColor: "primary.main",
                      }}
                    />
                  )}
                </Box>
                <SoftTypography
                  color={darkMode ? "white" : "text"}
                  sx={{
                    fontFamily: "DM Sans",
                    color: "#98A2B3",
                    lineHeight: 1,
                    fontSize: "11px",
                    fontWeight: 500,
                    letterSpacing: "0.02em",
                  }}
                >
                  Points
                </SoftTypography>
              </Box>
            </Box>
          </Box>
        </Box>
      </section>

      <Transition in={show} timeout={duration} unmountOnExit>
        {(state) => (
          <SoftBox
            ref={expandRef}
            sx={{
              ...defaultStyle,
              ...transitionStyles[state],
              marginY: { mini: "0.5rem", md: "1rem" },
              paddingX: {
                mini: 0,
                xxs: "0.5rem",
                xs: "1rem",
              },
              paddingY: { mini: "0.25rem", xs: "0.5", md: "1rem" },
            }}
          >
            <ReportsDoughnutChart
              title={`${!userFirstName ? "User's" : userFirstName} KPIs`}
              count={{ number: points, text: "POINTS" }}
              versusYetiScores={versusYetiScores}
              chart={{
                labels: kpiTitle,
                labelItems: kpiLabels,
                ids: kpiIds,
                types: kpiTypes,
                normalValues: kpiNormalValues,
                pointsValue: kpiValue,
                isYeti: userId,
                versusYetiScores,
                datasets: {
                  label: "KPI",
                  backgroundColors: [
                    "primary",
                    "secondary",
                    "info",
                    "success",
                    "warning",
                  ],
                  // `data` drives slice angles — uses floored display weights
                  // so even small-point KPIs stay visible. `actualPoints` and
                  // `amounts` carry the real values for the hover tooltip.
                  data: kpiDisplayWeights,
                  actualPoints: kpiValue,
                  amounts: kpiLabels,
                },
              }}

              userId={userId}
              link={!isYeti && !isViewOnly}
              tooltip="See player for more info"
              playerName={`${!userFirstName ? "User's" : userFirstName}'s`}
              playerStatus={playerStatus}
            />
          </SoftBox>
        )}
      </Transition>
    </SoftBox>
  );
}

export default ProgressBar;
